import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Search, ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const NotFoundPage = () => {
  const location = useLocation();
  const [attemptedUrl, setAttemptedUrl] = useState(location.pathname);

  useEffect(() => {
    // Check if this was a redirect from static 404.html
    const redirectUrl = sessionStorage.getItem('404_redirect_url');
    if (redirectUrl) {
      setAttemptedUrl(redirectUrl);
      sessionStorage.removeItem('404_redirect_url');
    }

    // Set proper 404 status for crawlers (in case they execute JavaScript)
    if (typeof window !== 'undefined' && window.history) {
      window.history.replaceState({}, '', attemptedUrl);
    }
  }, [attemptedUrl]);

  return (
    <div>
      <SEO 
        title="Page Not Found (404) | Groundhogs Septics"
        robots="noindex, follow"
        description="The page you're looking for doesn't exist. Return to our homepage for septic installation services in Thunder Bay and Northwestern Ontario."
      />
      <Header />

      {/* 404 Content */}
      <section className="pt-32 pb-20 min-h-screen flex items-center" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* 404 Icon */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-32 h-32 rounded-full" style={{ backgroundColor: "var(--primary-orange)", opacity: 0.1 }}>
              <Search size={64} style={{ color: "var(--primary-orange)" }} />
            </div>
          </div>

          {/* Error Code */}
          <h1 className="text-8xl font-bold mb-4" style={{ color: "var(--primary-orange)" }}>
            404
          </h1>

          {/* Error Message */}
          <h2 className="text-4xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
            Page Not Found
          </h2>
          <p className="text-xl mb-8" style={{ color: "var(--text-light)" }}>
            Sorry, we couldn't find the page you're looking for. It may have been moved, deleted, or never existed.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-4 rounded-md font-medium text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: "var(--primary-orange)" }}
            >
              <Home size={20} className="mr-2" />
              Go to Homepage
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 rounded-md font-medium border-2 hover:bg-gray-50 transition-colors"
              style={{ color: "var(--dark-navy)", borderColor: "var(--dark-navy)" }}
            >
              <ArrowLeft size={20} className="mr-2" />
              Contact Us
            </Link>
          </div>

          {/* Helpful Links */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold mb-6" style={{ color: "var(--dark-navy)" }}>
              Popular Pages
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/services"
                className="p-4 rounded-md hover:bg-gray-50 transition-colors text-left border-2 border-gray-200"
              >
                <h4 className="font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                  Our Services
                </h4>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Traditional & Eljen septic systems
                </p>
              </Link>
              <Link
                to="/service-areas"
                className="p-4 rounded-md hover:bg-gray-50 transition-colors text-left border-2 border-gray-200"
              >
                <h4 className="font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                  Service Areas
                </h4>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Thunder Bay & Northwestern Ontario
                </p>
              </Link>
              <Link
                to="/septic-calculator"
                className="p-4 rounded-md hover:bg-gray-50 transition-colors text-left border-2 border-gray-200"
              >
                <h4 className="font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                  Cost Calculator
                </h4>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Get instant pricing estimate
                </p>
              </Link>
              <Link
                to="/blog"
                className="p-4 rounded-md hover:bg-gray-50 transition-colors text-left border-2 border-gray-200"
              >
                <h4 className="font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                  Blog
                </h4>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Septic installation tips & guides
                </p>
              </Link>
              <Link
                to="/faq"
                className="p-4 rounded-md hover:bg-gray-50 transition-colors text-left border-2 border-gray-200"
              >
                <h4 className="font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                  FAQ
                </h4>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Common questions answered
                </p>
              </Link>
              <Link
                to="/contact"
                className="p-4 rounded-md hover:bg-gray-50 transition-colors text-left border-2 border-gray-200"
              >
                <h4 className="font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                  Contact
                </h4>
                <p className="text-sm" style={{ color: "var(--text-light)" }}>
                  Get your free estimate
                </p>
              </Link>
            </div>
          </div>

          {/* Help Text */}
          <p className="mt-8 text-sm" style={{ color: "var(--text-light)" }}>
            Need help? Call us at <a href="tel:1-888-907-3777" className="font-bold" style={{ color: "var(--primary-orange)" }}>1-888-907-3777</a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NotFoundPage;
